/**
 * Modal de Crear/Editar Sección de Campos Personalizados.
 *
 * Este componente proporciona una interfaz modal para crear y editar
 * secciones que agrupan campos personalizados en los formularios
 * de registro de eventos.
 *
 * @author Kiro AI Assistant
 * @date [Fecha de creación: 2025-08-20]
 * @updated [Fecha de última modificación: 2025-08-20]
 * @updatedBy Kiro AI Assistant
 */

import React from "react";
import { Modal, Card, Icon, Divider, TextField, Grid } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

/**
 * Componente modal para crear o editar secciones de campos personalizados.
 *
 * @param {Object} props - Propiedades del componente.
 * @param {boolean} props.open - Controla si el modal está abierto.
 * @param {Function} props.onClose - Función que se ejecuta al cerrar el modal.
 * @param {Object|null} props.editingSection - Sección en edición o null si es nueva.
 * @param {string} props.sectionName - Nombre actual de la sección.
 * @param {Function} props.onNameChange - Función para actualizar el nombre.
 * @param {Function} props.onSave - Función que se ejecuta al guardar.
 * @param {boolean} props.saving - Indica si se está guardando actualmente.
 * @returns {JSX.Element} Modal con formulario de sección.
 */
export default function SectionModal({
  open,
  onClose,
  editingSection,
  sectionName,
  onNameChange,
  onSave,
  saving,
}) {
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: "500px",
    borderRadius: "20px",
    boxShadow: 24,
    p: 4,
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-section-title"
      aria-describedby="modal-section-description"
    >
      <Card sx={style}>
        <MDBox
          mt={-7}
          color="white"
          bgColor="info"
          variant="gradient"
          borderRadius="lg"
          p={2}
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <MDTypography variant="h5" color="white">
            {editingSection ? "Editar Sección" : "Crear Sección"}
          </MDTypography>
          <Icon
            sx={{
              fontSize: "2em",
              stroke: "currentColor",
              strokeWidth: "2px",
              cursor: "pointer",
              color: "white",
            }}
            onClick={onClose}
          >
            close
          </Icon>
        </MDBox>

        <Divider sx={{ my: 0 }} />

        <MDBox pt={4} pb={3} px={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre de la Sección"
                value={sectionName}
                onChange={(e) => onNameChange(e.target.value)}
                required
                variant="outlined"
                size="medium"
                placeholder="Ej: Información Personal, Datos Laborales..."
                InputLabelProps={{
                  sx: { fontSize: "1rem" },
                }}
              />
            </Grid>
          </Grid>

          <MDBox mt={4} display="flex" justifyContent="flex-end" gap={2}>
            <MDButton onClick={onClose} color="error" disabled={saving}>
              Cancelar
            </MDButton>
            <MDButton onClick={onSave} color="success" disabled={saving}>
              {saving ? "Guardando..." : editingSection ? "Actualizar" : "Crear"}
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>
    </Modal>
  );
}
